import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  Bot, Newspaper, CalendarClock, Landmark, LineChart, Gauge,
  ArrowRight, Check, Crown, Sparkles, TrendingUp, ShieldCheck,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

const FEATURES = [
  { icon: Bot, k: "feat.agent" },
  { icon: Newspaper, k: "feat.news" },
  { icon: CalendarClock, k: "feat.cal" },
  { icon: Landmark, k: "feat.cot" },
  { icon: LineChart, k: "feat.macro" },
  { icon: Gauge, k: "feat.risk" },
];

function TopBar() {
  const { t } = useLang();
  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-white/5 bg-[#0A0C10]/80 backdrop-blur-xl">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3">
        <div className="flex items-center gap-2.5">
          <img src="/logo.png" alt="Golden Terminal" className="h-8 w-8 object-contain" />
          <div>
            <div className="font-heading text-sm font-bold leading-none text-white">GOLDEN</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-amber-500/80">Terminal</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          <Link to="/login" data-testid="landing-login" className="rounded-lg px-3 py-2 text-sm font-medium text-slate-300 hover:text-white">{t("landing.login")}</Link>
          <Link to="/register" data-testid="landing-register" className="rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-2 text-sm font-bold text-black transition-opacity hover:opacity-90">{t("landing.start")}</Link>
        </div>
      </div>
    </header>
  );
}

export default function Landing() {
  const { user } = useAuth();
  const { t, lang } = useLang();
  const [plans, setPlans] = useState([]);
  useEffect(() => { api.get(`/payments/plans?lang=${lang}`).then(({ data }) => setPlans(data.plans)).catch(() => {}); }, [lang]);
  if (user) return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen bg-[#0A0C10] text-slate-200" data-testid="landing-page">
      <TopBar />

      {/* Hero */}
      <section className="relative overflow-hidden px-5 pt-36 pb-24">
        <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(60% 50% at 50% 0%, rgba(245,158,11,0.14), transparent 70%)" }} />
        <div className="pointer-events-none absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)", backgroundSize: "44px 44px" }} />
        <div className="relative mx-auto max-w-3xl text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/30 bg-amber-500/10 px-3 py-1 text-xs font-medium text-amber-300">
            <Sparkles className="h-3.5 w-3.5" /> {t("landing.badge")}
          </span>
          <h1 className="mt-6 font-heading text-4xl font-extrabold leading-tight text-white sm:text-5xl lg:text-6xl">
            {t("landing.h1a")}<br /><span className="bg-gradient-to-r from-amber-300 to-orange-500 bg-clip-text text-transparent">{t("landing.h1b")}</span>
          </h1>
          <p className="mx-auto mt-5 max-w-xl text-base text-slate-400">
            {t("landing.sub")}
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link to="/register" data-testid="hero-cta" className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-3 text-sm font-bold text-black transition-opacity hover:opacity-90">
              {t("landing.createAccount")} <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/login" className="rounded-lg border border-slate-700 px-6 py-3 text-sm font-semibold text-slate-200 hover:bg-slate-800/60">{t("landing.haveAccount")}</Link>
          </div>
          <p className="mt-4 flex items-center justify-center gap-1.5 text-xs text-slate-600"><ShieldCheck className="h-3.5 w-3.5" /> {t("landing.noCommit")}</p>
        </div>
      </section>

      {/* Aperçu animé du terminal */}
      <section className="relative px-5 pb-8">
        <div className="pointer-events-none absolute inset-x-0 top-1/3 mx-auto h-64 max-w-4xl" style={{ background: "radial-gradient(50% 60% at 50% 50%, rgba(245,158,11,0.16), transparent 70%)" }} />
        <div className="relative mx-auto max-w-5xl gt-rise">
          <div className="gt-float overflow-hidden rounded-2xl border border-slate-700/60 bg-[#0E1118] shadow-2xl shadow-black/50 ring-1 ring-white/5">
            <div className="flex items-center gap-1.5 border-b border-slate-800 bg-[#0B0E14] px-4 py-2.5">
              <span className="h-3 w-3 rounded-full bg-red-500/70" />
              <span className="h-3 w-3 rounded-full bg-amber-500/70" />
              <span className="h-3 w-3 rounded-full bg-emerald-500/70" />
              <span className="ml-3 rounded bg-[#11141A] px-2 py-0.5 text-[11px] text-slate-500">goldenterminal.ca/dashboard</span>
            </div>
            <img src="/terminal-preview.png" alt="Aperçu du tableau de bord Golden Terminal" className="w-full" loading="lazy" data-testid="terminal-preview" />
          </div>

          {/* Cartes flottantes */}
          <div className="gt-float-slow absolute -left-3 top-1/3 hidden rounded-xl border border-amber-500/30 bg-[#0E1118]/95 p-3 shadow-xl backdrop-blur sm:block">
            <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-amber-300"><Landmark className="h-3 w-3" /> COT · IA</div>
            <p className="mt-1 max-w-[180px] text-[11px] leading-snug text-slate-300">« Les spéculateurs se retournent short sur l'or… »</p>
          </div>
          <div className="gt-float absolute -right-3 bottom-10 hidden rounded-xl border border-emerald-500/30 bg-[#0E1118]/95 p-3 shadow-xl backdrop-blur sm:block">
            <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-300"><Gauge className="h-3 w-3" /> Sentiment</div>
            <p className="mt-1 text-lg font-bold text-white">52 <span className="text-xs font-normal text-slate-500">/100 · Neutre</span></p>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="px-5 py-16">
        <div className="mx-auto max-w-6xl">
          <h2 className="text-center font-heading text-2xl font-bold text-white">{t("landing.featuresTitle")}</h2>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f) => (
              <div key={f.k} className="group rounded-2xl border border-slate-800 bg-[#0E1118] p-6 transition-colors hover:border-amber-500/30">
                <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-amber-500/10 text-amber-400 transition-transform group-hover:scale-110">
                  <f.icon className="h-5 w-5" />
                </div>
                <h3 className="font-heading text-base font-semibold text-white">{t(`${f.k}.t`)}</h3>
                <p className="mt-1.5 text-sm text-slate-400">{t(`${f.k}.d`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="px-5 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="text-center font-heading text-2xl font-bold text-white">{t("landing.pricingTitle")}</h2>
          <p className="mt-2 text-center text-sm text-slate-400">{t("landing.pricingSub1")} <span className="text-emerald-400">{t("landing.pricingSub2")}</span>{t("landing.pricingSub3")}</p>
          <div className="mt-10 grid gap-5 md:grid-cols-2">
            {plans.map((p) => {
              const premium = p.id === "premium";
              return (
                <div key={p.id} className={`relative rounded-2xl border ${premium ? "border-amber-500/60" : "border-blue-600/40"} bg-[#0E1118] p-6`}>
                  {premium && <span className="absolute -top-3 right-5 rounded-full bg-amber-500 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-black">{t("landing.recommended")}</span>}
                  <div className={`mb-3 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${premium ? "bg-amber-500/15 text-amber-300" : "bg-blue-500/15 text-blue-300"}`}>
                    {premium ? <Crown className="h-3.5 w-3.5" /> : <Sparkles className="h-3.5 w-3.5" />} {p.label}
                  </div>
                  <div className="flex items-end gap-1">
                    <span className="font-heading text-4xl font-bold text-white">${p.monthly}</span>
                    <span className="mb-1 text-sm text-slate-500">{t("landing.perMonth")}</span>
                  </div>
                  <p className="mb-2 text-xs text-emerald-400">{t("landing.orYear")} ${p.annual}{t("landing.year")} · −{p.annual_savings_pct}%</p>
                  <ul className="my-5 space-y-2">
                    {p.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-300"><Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" /> {f}</li>
                    ))}
                  </ul>
                  <Link to="/register" data-testid={`landing-plan-${p.id}`} className={`flex w-full items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-semibold transition-opacity hover:opacity-90 ${premium ? "bg-amber-500 text-black" : "bg-blue-600 text-white"}`}>
                    {t("landing.choose")} {p.label}
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-5 py-20">
        <div className="mx-auto max-w-3xl rounded-3xl border border-amber-500/20 bg-gradient-to-br from-[#1a1206] to-[#0E1118] p-10 text-center">
          <TrendingUp className="mx-auto mb-4 h-8 w-8 text-amber-400" />
          <h2 className="font-heading text-2xl font-bold text-white sm:text-3xl">{t("landing.ctaTitle")}</h2>
          <p className="mx-auto mt-3 max-w-md text-sm text-slate-400">{t("landing.ctaSub")}</p>
          <Link to="/register" className="mt-6 inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-3 text-sm font-bold text-black transition-opacity hover:opacity-90">
            {t("landing.createAccount")} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <footer className="border-t border-white/5 px-5 py-8">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-3 text-xs text-slate-600 sm:flex-row">
          <div className="flex items-center gap-2"><img src="/logo.png" alt="" className="h-5 w-5 object-contain" /> Golden Terminal © {new Date().getFullYear()}</div>
          <div className="flex gap-4">
            <Link to="/login" className="hover:text-slate-300">Connexion</Link>
            <Link to="/register" className="hover:text-slate-300">Inscription</Link>
            <Link to="/cgu" className="hover:text-slate-300" data-testid="footer-cgu">CGU</Link>
            <Link to="/confidentialite" className="hover:text-slate-300" data-testid="footer-privacy">Confidentialité</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
